import { FC, useMemo } from "react";
import { ICPEmployeeCountWinLossDistribution } from "../../../../apis/companySearch/icpDistribution";
import { getSegmentNameFromRange } from "../../../Workspace/View/MarketLandscape/utils";
import { hasWinLossData } from "../../utils";
import { GenericICPWinLossChart } from "./GenericICPWinLossChart";
import { DEFAULT_API_EMPLOYEE_RANGES } from "../../../Workspace/View/constants";

interface ICPEmployeeCountWinLossChartProps {
  /** Win/loss distribution data from API */
  data: ICPEmployeeCountWinLossDistribution[];
  /** Height of the chart container */
  height?: string;
  /** Optional total count to display (overrides calculated total from data) */
  totalCount?: number;
  /** Whether the chart is loading */
  isLoading?: boolean;
}

/**
 * Component that displays win/loss employee count distribution chart with stacked columns
 * Each column shows win and loss segments that total 100%
 */
export const ICPEmployeeCountWinLossChart: FC<
  ICPEmployeeCountWinLossChartProps
> = ({ data, height = "350px", totalCount, isLoading = false }) => {
  const getCategoryName = useMemo(
    () => (item: ICPEmployeeCountWinLossDistribution) => {
      const range = {
        min: item.employeesRangeMin || 0,
        max: item.employeesRangeMax || Number.POSITIVE_INFINITY,
      };
      return getSegmentNameFromRange(range, DEFAULT_API_EMPLOYEE_RANGES);
    },
    []
  );

  const sortData = useMemo(
    () =>
      (
        a: ICPEmployeeCountWinLossDistribution,
        b: ICPEmployeeCountWinLossDistribution
      ) => {
        const minA = a.employeesRangeMin || 0;
        const minB = b.employeesRangeMin || 0;
        return minA - minB; // Sort by minimum employee count (ascending)
      },
    []
  );

  // Show skeleton when loading, or don't render if there's no meaningful win/loss data when not loading
  if (!isLoading && !hasWinLossData(data)) {
    return null;
  }

  return (
    <GenericICPWinLossChart
      data={data}
      getCategoryName={getCategoryName}
      height={height}
      totalCount={totalCount}
      sortData={sortData}
      segment="Employees"
      isLoading={isLoading}
    />
  );
};
